/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState } from 'react';
import { Link, graphql } from 'gatsby';
import { GatsbyImage, getImage, withArtDirection } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import { useWindowSize } from 'hooks';
import { Carousel as HeroCarousel } from 'react-responsive-carousel';
import { getAltTagFromFileName, initFadeInAnimation, slugify } from 'utils';
import {
  Testimonials,
  SEO,
  MultiColumnCarousel,
  StaircaseCard,
  Button,
  ProductSpotlights,
  ThreePoints,
  TextAndImageBlock,
  GuidesAndManuals
} from 'components';

function HomePage({ data }) {
  const {
    carouselImage1Desktop,
    carouselImage1Mobile,
    carouselImage2Desktop,
    carouselImage2Mobile,
    carouselImage3Desktop,
    carouselImage3Mobile,
    oakStairs,
    stairCollections,
    doors
  } = data;
  const [currentSlide, setCurrentSlide] = useState(0);
  const { windowWidth } = useWindowSize();
  const carouselSlide1Images = withArtDirection(getImage(carouselImage1Mobile), [
    {
      media: '(min-width: 768px)',
      image: getImage(carouselImage1Desktop)
    }
  ]);
  const carouselSlide2Images = withArtDirection(getImage(carouselImage2Mobile), [
    {
      media: '(min-width: 768px)',
      image: getImage(carouselImage2Desktop)
    }
  ]);
  const carouselSlide3Images = withArtDirection(getImage(carouselImage3Mobile), [
    {
      media: '(min-width: 768px)',
      image: getImage(carouselImage3Desktop)
    }
  ]);

  const ourProducts = [...stairCollections.nodes, doors].map((node) => {
    const { id, frontmatter } = node;
    const { title, slug, shortDescription, cardImage } = frontmatter;
    return (
      <StaircaseCard
        key={id}
        id={id}
        slug={title === 'Doors' ? '/doors/' : `/stairs/${slug}`}
        image={cardImage.childImageSharp.gatsbyImageData}
        title={title}
        description={shortDescription}
        alt={getAltTagFromFileName(cardImage.name)}
      />
    );
  });

  return (
    <>
      <Hero>
        <StyledHeroCarousel
          infiniteLoop
          autoPlay
          onChange={setCurrentSlide}
          selectedItem={currentSlide}
          showArrows={false}
          showStatus={false}
          showThumbs={false}
          stopOnHover={false}
          showIndicators={false}
          interval={7000}
          transitionTime={500}
          preventMovementUntilSwipeScrollTolerance
          swipeScrollTolerance={50}>
          <Slide>
            <StyledGatsbyImage
              image={carouselSlide1Images}
              alt={getAltTagFromFileName(carouselImage1Desktop.name)}
              loading="eager"
            />
            <h2>Premium Staircraft</h2>
            <h1>
              Staircases To Elevate
              <br />
              Your Home
            </h1>
            <p>
              From sleek and modern designs to timeless classics, our staircases are crafted with
              precision and passion to elevate your home to new heights of elegance and style.
            </p>
            <Button to="/stairs/" style={{ width: '184px' }}>
              Explore Staircases
            </Button>
          </Slide>
          <Slide>
            <StyledGatsbyImage
              image={carouselSlide2Images}
              alt={getAltTagFromFileName(carouselImage2Desktop.name)}
              loading="eager"
            />
            <h2>Stair & Door Parts</h2>
            {windowWidth < 1130 ? (
              <h1>
                Refurbish With Style
                <br />
                Transform Your Space With Our Premium Stair And Door Parts
              </h1>
            ) : (
              <h1>
                Refurbish With Style
                <br />
                Transform Your Space With Our
                <br />
                Premium Stair And Door Parts
              </h1>
            )}
            <p>
              Choose a simple yet impactful upgrade. Why not replace old door and stair parts with
              our premium, stylish alternatives.
            </p>
            <Button to="/stair-and-door-parts/" style={{ width: '164px' }}>
              View Products
            </Button>
          </Slide>
          <Slide>
            <StyledGatsbyImage
              image={carouselSlide3Images}
              alt={getAltTagFromFileName(carouselImage3Desktop.name)}
              loading="eager"
            />
            <h2>Signature Doors</h2>
            {windowWidth < 940 ? (
              <h1>Unlock Your Dream Home With Our Inspired Door Designs</h1>
            ) : (
              <h1>
                Unlock Your Dream Home
                <br />
                With Our Inspired Door Designs
              </h1>
            )}
            <p>
              Blending timeless elegance with modern flair, our collection includes classic,
              decorative, shaker, sliding, oak, contemporary, and fire doors.
            </p>
            <Button to="/doors/" style={{ width: '164px' }}>
              Explore Doors
            </Button>
          </Slide>
        </StyledHeroCarousel>
        <ControlDots>
          <Dot type="button" selected={currentSlide === 0} onClick={() => setCurrentSlide(0)} />
          <Dot type="button" selected={currentSlide === 1} onClick={() => setCurrentSlide(1)} />
          <Dot type="button" selected={currentSlide === 2} onClick={() => setCurrentSlide(2)} />
        </ControlDots>
      </Hero>
      <Grid>
        <ThreePoints />
        <ProductSpotlights />
      </Grid>
      {ourProducts.length && (
        <StyledMultiColumnCarousel
          title="Our Products"
          cards={ourProducts}
          backgroundColor="#fff"
          responsive={{
            desktop: {
              breakpoint: { max: 10000, min: 1300 },
              items: 3
            },
            tablet: {
              breakpoint: { max: 1299, min: 914 },
              items: 2
            },
            mobile: {
              breakpoint: { max: 913, min: 0 },
              items: 1
            }
          }}
        />
      )}
      <Grid $backgroundColor={windowWidth < 1200 ? 'var(--connolly-grey)' : '#fff'}>
        <TextAndImageBlock
          image={oakStairs.childImageSharp.gatsbyImageData}
          imageTitle="Oak Stairs"
          imageAlt={getAltTagFromFileName(oakStairs.name)}
          paddingTop={windowWidth < 1200 ? '39px' : '153px'}>
          <h3>
            Founded in 2002, and located in Dublin, we have established ourselves as one of
            Ireland&apos;s leading suppliers of handmade staircases and interior doors.
          </h3>
          <p>
            Our team of skilled artisans combines years of expertise with a keen eye for detail,
            ensuring every product is not only aesthetically pleasing but also built to last. From
            the initial design to the final installation, we work closely with you to bring your
            vision to life, offering personalised solutions that reflect your unique style and meet
            your specific needs.
          </p>
          <Button to="/about-us/" style={{ width: '145px' }}>
            About Us
          </Button>
        </TextAndImageBlock>
      </Grid>
      {/* <Grid $backgroundColor="#fff">
        <GuidesAndManuals />
      </Grid> */}
      <Testimonials className="fade-in" backgroundColor="#fff" />
    </>
  );
}

const Grid = styled.section`
  background-color: ${({ $backgroundColor }) => $backgroundColor || 'var(--connolly-grey)'};
  column-gap: 20px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 100px));
  justify-content: center;
  padding: 0 1.25rem;

  @media (width >= 768px) {
    padding: 0 0.625rem;
  }
`;

const Hero = styled.section`
  column-gap: 20px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 100px));
  height: 734px;
  justify-content: center;
  padding: 0 0.75rem;
  position: relative;

  @media (width >= 390px) {
    padding: 0 1.5rem;
  }

  @media (width >= 768px) {
    padding: 0 1.5rem 0 0.625rem;
    height: 719px;
  }

  @media (width >= 1200px) {
    padding: 0 0.625rem;
    height: 719px;
  }
`;

const ControlDots = styled.div`
  display: flex;
  gap: 0.5rem;
  grid-column: 1/3;
  justify-content: center;
  position: absolute;
  top: 37px;

  @media (width >= 768px) {
    grid-column: 2/4;
  }
`;

const Dot = styled.button`
  background: #c9c9c9;
  border: 0.125rem solid #c9c9c9;
  border-radius: 100%;
  box-shadow: none;
  cursor: pointer;
  height: 0.625rem;
  opacity: 0.6;
  transition:
    background 0.25s,
    border 0.25s,
    opacity 0.25s;
  width: 0.625rem;

  ${({ selected }) =>
    selected &&
    css`
      background: var(--connolly-red);
      border: 0.125rem solid var(--connolly-red);
      opacity: 1;
    `}
`;

const StyledHeroCarousel = styled(HeroCarousel)`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;

  .carousel-slider,
  .slider-wrapper,
  ul.slider {
    height: 100%;
  }

  li:nth-of-type(1) h1 {
    font-size: 3rem;

    @media (width >= 768px) {
      font-size: 4rem;
    }
  }

  li:nth-of-type(2) h1 {
    font-size: 2.125rem;

    @media (width >= 590px) {
      font-size: 3rem;
    }

    @media (width >= 1200px) {
      font-size: 4rem;
    }
  }

  li:nth-of-type(3) h1 {
    font-size: 2.125rem;

    @media (width >= 590px) {
      font-size: 3rem;
    }

    @media (width >= 768px) {
      font-size: 3.5rem;
    }

    @media (width >= 1200px) {
      font-size: 4rem;
    }
  }
`;

const Slide = styled.div`
  column-gap: 20px;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 100px));
  grid-template-rows: repeat(3, auto);
  height: 100%;
  padding: 0 0.75rem;
  place-content: start center;
  width: 100%;

  @media (width >= 390px) {
    padding: 0 0.5rem 0 1.5rem;
  }

  @media (width >= 768px) {
    padding: 0 1.5rem 0 0.625rem;
  }

  @media (width >= 1200px) {
    padding: 0 0.625rem;
  }

  h1,
  h2,
  p {
    color: #fff;
    text-align: left;
  }

  h1,
  h2,
  p,
  a {
    grid-column: 1/13;

    @media (width >= 768px) {
      grid-column: 2/13;
    }
  }

  a {
    white-space: nowrap !important;
  }

  h2 {
    font-family: 'Open Sans', 'Segoe UI', Tahoma, sans-serif;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 76px 0 15px -2px;
  }

  h1 {
    font-size: 3rem;
    font-weight: 400;
    line-height: 1.313em;
    margin-bottom: 0.563em;
    margin-left: -0.078em;

    @media (width >= 768px) {
      font-size: 4rem;
    }
  }

  p {
    font-size: 1.25rem;
    line-height: 1.4em;
    margin-bottom: 32px;
    margin-left: -1px;
    max-width: 655px;
  }
`;

const StyledMultiColumnCarousel = styled(MultiColumnCarousel)`
  padding-bottom: 112px;
  width: 100%;

  > h3 {
    padding: 1.25em 0 0.85em;

    @media (width >= 768px) {
      padding: 1.906em 0 1.1em;
    }
  }

  > div {
    /* Width of Arrow Buttons */
    max-width: 360px;

    /* Width of Slides container */
    > div {
      max-width: 360px;
    }
  }

  @media (width >= 380px) {
    > div {
      max-width: 440px;

      > div {
        max-width: 380px;
      }
    }
  }

  @media (width >= 914px) {
    padding-bottom: 39px;

    > div {
      max-width: 820px;

      > div {
        max-width: 760px;
      }
    }
  }

  @media (width >= 1300px) {
    > div {
      max-width: 1200px;

      > div {
        max-width: 1140px;
      }
    }
  }

  .react-multi-carousel-dot button {
    border: none;
    width: 0.625rem;
    height: 0.625rem;
  }

  .react-multi-carousel-dot--active button {
    background-color: var(--connolly-red);
  }

  .react-multi-carousel-item a {
    height: 30.375rem;
    max-width: 22.5rem;
    width: 100%;

    .gatsby-image-wrapper {
      height: 15rem;
    }
  }
`;

const StyledGatsbyImage = styled(GatsbyImage)`
  height: 100%;
  left: 0;
  position: absolute !important;
  top: 0;
  width: 100%;
  z-index: -1;
`;

export const query = graphql`
  query {
    carouselImage1Desktop: file(
      relativePath: {
        eq: "hero-images/homepage-carousel/desktop/cut-string-closed-riser-white-stairs.png"
      }
    ) {
      name
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
      }
    }
    carouselImage1Mobile: file(
      relativePath: {
        eq: "hero-images/homepage-carousel/mobile/cut-string-closed-riser-white-stairs.png"
      }
    ) {
      name
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
      }
    }
    carouselImage2Desktop: file(
      relativePath: { eq: "hero-images/homepage-carousel/desktop/cut-string-walnut-stairs.png" }
    ) {
      name
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
      }
    }
    carouselImage2Mobile: file(
      relativePath: { eq: "hero-images/homepage-carousel/mobile/cut-string-walnut-stairs.png" }
    ) {
      name
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
      }
    }
    carouselImage3Desktop: file(
      relativePath: {
        eq: "hero-images/homepage-carousel/desktop/lamp-and-chair-beside-white-door.png"
      }
    ) {
      name
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
      }
    }
    carouselImage3Mobile: file(
      relativePath: {
        eq: "hero-images/homepage-carousel/mobile/lamp-and-chair-beside-white-door.png"
      }
    ) {
      name
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
      }
    }
    oakStairs: file(relativePath: { eq: "about-us/oak-closed-string-closed-riser-stairs.png" }) {
      name
      childImageSharp {
        gatsbyImageData(width: 590, height: 590, formats: [AUTO, WEBP, AVIF])
      }
    }
    stairCollections: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/local-data/stairs/collections/" } }
    ) {
      nodes {
        id
        frontmatter {
          title
          slug
          shortDescription
          cardImage {
            name
            childImageSharp {
              gatsbyImageData(width: 360, height: 240, formats: [AUTO, WEBP, AVIF])
            }
          }
        }
      }
    }
    doors: markdownRemark(frontmatter: { productCollectionName: { eq: "Doors" } }) {
      id
      frontmatter {
        productCollectionName
        title
        shortDescription
        cardImage {
          name
          childImageSharp {
            gatsbyImageData(width: 360, height: 240, formats: [AUTO, WEBP, AVIF])
          }
        }
      }
    }
  }
`;

export function Head() {
  return (
    <SEO
      title="Stairs Ireland - Quality Stairs at Affordable Prices"
      description="Founded in 2002, and located in Dublin, we have established ourselves as one of Ireland's leading suppliers of handmade staircases and interior doors. Discover expert craftsmanship, extensive range, and superior service.">
      <script type="application/ld+json">
        {`{
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Stairs Ireland",
            "item": "https://www.connollystairs.ie/"
          }
        ]
      }`}
      </script>
    </SEO>
  );
}

export default HomePage;
